<template>
  <div class="country-selector" ref="selectorWrapper">
    <div class="selected-country" @click="toggleDropdown">
      <!-- <span class="flag">{{ selectedCountry.flag }}</span> -->
         <span v-if="selectedCountry.code !== 'OTH'" class="fi" :class="`fi-${selectedCountry.code.toLowerCase()}`"></span>
        <span v-else class="flag">🌐</span>
 
      <i class="fas fa-chevron-down"></i>
    </div>
    
    <div v-if="showDropdown" class="country-dropdown">
      <div 
        v-for="country in countries" 
        :key="country.code"
        class="country-item"
        :class="{ active: country.code === selectedCountry.code }"
        @click="selectCountry(country)"
      >
        <span v-if="country.code !== 'OTH'" class="fi" :class="`fi-${country.code.toLowerCase()}`"></span>
        <span v-else class="flag">🌐</span>
        <span class="name">{{ country.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInst from '@/utils/axiosinstance';
import { trackInteraction } from '@/utils/trackInteraction';
import { toast } from 'vue-sonner';
import { EventBus } from '@/services/EventBus';
import { getCode as getCountryCodeByName, getName as getCountryNameByCode } from 'country-list';

export default {
  name: 'CountrySelector',
  data() {
    return {
      showDropdown: false,
      selectedCountry: { code: 'US', name: 'United States', flag: '🇺🇸' },
      countries: [
        { code: 'US', name: 'United States', flag: '🇺🇸' },
        { code: 'CA', name: 'Canada', flag: '🇨🇦' },
        { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
        { code: 'DE', name: 'Germany', flag: '🇩🇪' },
        { code: 'FR', name: 'France', flag: '🇫🇷' },
        { code: 'AE', name: 'UAE', flag: '🇦🇪' },
        { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
        { code: 'IN', name: 'India', flag: '🇮🇳' },
        { code: 'CN', name: 'China', flag: '🇨🇳' },
        { code: 'JP', name: 'Japan', flag: '🇯🇵' },
        { code: 'AU', name: 'Australia', flag: '🇦🇺' },
        { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
        { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
        { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
        { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
        { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
        { code: 'OM', name: 'Oman', flag: '🇴🇲' },
        { code: 'NP', name: 'Nepal', flag: '🇳🇵' },
        { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
        { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
        { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
        { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
        { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
        { code: 'OTH', name: 'Others', flag: '🌐' },
      ]
    };
  },
  mounted() {
    this.loadUserCountry();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    handleClickOutside(event) {
      if (this.$refs.selectorWrapper && !this.$refs.selectorWrapper.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    async loadUserCountry() {
      try {
        const userId = localStorage.getItem('userid');
        if (!userId) return;

        const { data } = await axiosInst.post('/api/userdetailslist', { userID: userId });
        const details = data?.items?.[0];
        if (details) {
          let backendCountry = details.preferredCountryName || '';
          let matchedCountry = this.countries.find(c => c.name === backendCountry);
          if (!matchedCountry) {
            // Try to get code from name or name from code
            let code = details.preferredCountry || getCountryCodeByName(backendCountry) || '';
            let name = backendCountry;
            if (!code && backendCountry) {
              code = getCountryCodeByName(backendCountry);
            }
            if (!name && code) {
              name = getCountryNameByCode(code);
            }
            if (code && name) {
              // Add to countries if not present
              let flag = this.getFlagEmoji(code);
              matchedCountry = { code, name, flag };
              this.countries.push(matchedCountry);
            }
          }
          if (matchedCountry) {
            this.selectedCountry = matchedCountry;
          } else {
            const others = this.countries.find(c => c.code === 'OTH');
            this.selectedCountry = others || { code: 'OTH', name: 'Others', flag: '🌐' };
          }
          // Seed EventBus so the user's saved country preference is applied to
          // the *initial* listings fetch — otherwise the first load on /rentals,
          // /jobs etc. ignores country and only later changes filter correctly.
          if (this.selectedCountry.code !== 'OTH' && !EventBus.selectedCountryName) {
            EventBus.selectedCountryName = this.selectedCountry.name;
          }
        }
      } catch (error) {
        console.error('Failed to load country preference:', error);
      }
    },

    getFlagEmoji(code) {
      // Returns emoji flag for a given country code (A-Z only)
      if (!code || code.length !== 2) return '🌐';
      const codePoints = code.toUpperCase().split('').map(c =>  127397 + c.charCodeAt());
      return String.fromCodePoint(...codePoints);
    },
    selectCountry(country) {
      trackInteraction('navbar', 'country-select', { label: country && country.name, role: 'user' });
      this.selectedCountry = country;
      this.showDropdown = false;
      const newCountryName = country.name === "Others" ? "" : country.name;
      // No-op when the user re-selects the active country
      if (EventBus.selectedCountryName === newCountryName) return;
      // Break out of sticky AI search so the country filter actually applies
      EventBus.clearSearchQuery();
      EventBus.userInteracted = true;
      EventBus.selectedCountryName = newCountryName;
      this.saveUserCountry(country);
    },
    async saveUserCountry(country) {
      try {
        const userId = localStorage.getItem('userid');
        if (!userId) return;

        const { data } = await axiosInst.post('/api/userdetailslist', { userID: userId });
        
        if (data?.items?.[0]) {
          await axiosInst.put('/api/updateuserdetails', {
            userDetailsID: data.items[0].userDetailsID,
            preferredCountry: country.code,
            preferredCountryName: country.name
          });
          toast.success(`Country set to ${country.name}`);
        }
      } catch (error) {
        console.error('Failed to save country preference:', error);
        toast.error('Failed to save country preference');
      }
    }
  }
};
</script>

<style scoped>
.country-selector {
  position: relative;
}

.selected-country {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.selected-country:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.flag {
  font-size: 20px;
  line-height: 1;
}

.country-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  min-width: 200px;
  max-height: 400px;
  overflow-y: auto;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  cursor: pointer;
  transition: background 0.2s;
}

.country-item:hover {
  background: #f3f4f6;
}

.country-item.active {
  background: #eff6ff;
  color: #3b82f6;
  font-weight: 500;
}

.country-item .name {
  font-size: 14px;
}

.fa-chevron-down {
  font-size: 12px;
  color: #6b7280;
}
</style>
